import type { DeviceSettings } from "@tandemdiabetes/sugarmate-api-react-client"
import { DayStat } from "@tandemdiabetes/sugarmate-api-react-client"
import {
  FieldSet,
  Legend,
  RadioButton,
  RadioButtonCheckMark,
  RadioButtons,
} from "@tandemdiabetes/sugarmate-react-components"
import type { ChangeEvent } from "react"
import { useCallback } from "react"

import styles from "./DayStats.module.scss"
import {
  usePolyglot,
  useFollowedUsers,
} from "src/contexts"
import { useInputPropsForValue } from "src/hooks"

const polyglotPrefix = "pages.settings.day_stats.day_stat."

const radioButtons: Array<DayStat> = [
  DayStat.NormalRange,
  DayStat.AverageGlucose,
  DayStat.BedTimeGlucose,
  DayStat.WakeUpGlucose,
  DayStat.TotalCarbs,
  DayStat.TotalUnits,
  DayStat.TotalCalories,
  DayStat.Exercise,
  DayStat.StandardDeviation,
]

interface Props {
  updatedAt?: string;
  updateDeviceSettings: (settings: Partial<DeviceSettings>) => void;
}

export function DayStatsSetting(props: Props) {
  const {
    updatedAt,
    updateDeviceSettings,
  } = props

  const polyglot = usePolyglot()
  const { currentDeviceSettings } = useFollowedUsers()
  const currentDayStat = currentDeviceSettings.day_stat

  const adapter = useCallback(
    (e: ChangeEvent<HTMLInputElement>) => e.target.value as DayStat,
    [],
  )

  const onValueChange = useCallback(
    (dayStat: DeviceSettings["day_stat"]) => {
      updateDeviceSettings({
        day_stat: dayStat,
      })
    },
    [updateDeviceSettings],
  )

  const {
    value,
    onChange,
  } = useInputPropsForValue({
    value: currentDayStat,
    onChange: onValueChange,
    adapter,
    updatedAt,
  })

  return (
    <FieldSet>
      <Legend>
        {polyglot.t(`${polyglotPrefix}label`)}
      </Legend>
      <div className={styles.dayStatsDescription}>
        {polyglot.t(`${polyglotPrefix}description`)}
      </div>
      <RadioButtons>
        {radioButtons.map(
          (dayMetric) => (
            <RadioButton
              key={dayMetric}
              name="day-metric"
              value={dayMetric}
              checked={value === dayMetric}
              onChange={onChange}
              htmlFor={dayMetric}
              required
              id={`radioBtn-${dayMetric}`}
            >
              <RadioButtonCheckMark/>
              {polyglot.t(`${polyglotPrefix}${dayMetric}`)}
            </RadioButton>
          ),
        )}
      </RadioButtons>
    </FieldSet>
  )
}
